import React from 'react';
import type { TransferRecord } from '@/data/transferRecord';
import TransferOrderChatPanel from '../TransferOrderChatPanel';
import { siteName } from './transferRecordView';

type TransferChatSectionProps = {
  record: TransferRecord;
};

/**
 * Chat: the transfer's own conversation (TransferOrderChatPanel). The old event-chat card could not
 * save here, because quote_chat_messages.event_id only accepts events. "Pop out" opens this section
 * in a separate window so the thread can stay open beside other work.
 */
export default function TransferChatSection({ record }: TransferChatSectionProps) {
  const { transfer } = record;
  const summary = React.useMemo(() => ({
    docNo: transfer.docNo,
    fromLocation: transfer.fromSite?.name ?? null,
    toLocation: transfer.toSite?.name ?? null,
  }), [transfer.docNo, transfer.fromSite?.name, transfer.toSite?.name]);

  const popOut = React.useCallback(() => {
    const url = `/transfers/${encodeURIComponent(transfer.id)}/chat`;
    const opened = window.open(url, `transfer-chat-${transfer.id}`, 'popup,width=480,height=760');
    opened?.focus();
  }, [transfer.id]);

  return (
    <div className="transfer-record-panel">
      <div className="transfer-record-panel__head">
        <div className="transfer-record-panel__heading">
          <h2 className="transfer-record-panel__title">Chat</h2>
          <p className="transfer-record-panel__intro">
            The transfer’s own thread for {siteName(transfer.fromSite, 'the shipping site')} and{' '}
            {siteName(transfer.toSite, 'the receiving site')}. Mention someone to notify them.
          </p>
        </div>
        <div className="transfer-record-panel__actions">
          <button type="button" className="btn sm" onClick={popOut} aria-label="Pop out the chat into its own window">
            Pop out
          </button>
        </div>
      </div>
      <div className="transfer-record-chat">
        <TransferOrderChatPanel transferId={transfer.id} transferSummary={summary} />
      </div>
    </div>
  );
}
